import React, { ReactNode, useEffect, useState } from 'react'

import { SxProps } from '@mui/material'

import { Severity } from 'utils/types'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import SectionHeader, {
  SectionHeaderProps
} from 'components_new/molecules/SectionHeader'

interface ContentBoxProps {
  border?: boolean
  children?: ReactNode
  collapse?: {
    active: boolean
    collapsed?: boolean
    defaultCollapsed?: boolean
    onCollapse?: () => void
    onExpand?: () => void
    unmountOnExit?: boolean
  } // requires header
  color?: 'brand' | 'default' | 'paper' | 'transparent' | Severity
  disablePadding?: boolean
  editMode?: boolean
  externalHeader?: SectionHeaderProps
  footer?: ReactNode
  gutterBottom?: boolean
  header?: ReactNode | SectionHeaderProps
  helperText?: string | ReactNode
  id?: string
  size?: 'small' | 'medium'
  sx?: SxProps
}

const ContentBox = React.forwardRef((props: ContentBoxProps, ref: any) => {
  const {
    border,
    children,
    collapse,
    color = 'paper',
    disablePadding,
    editMode,
    externalHeader,
    footer,
    gutterBottom,
    header,
    helperText,
    id,
    size,
    sx
  } = props

  const [collapsed, setCollapsed] = useState<boolean>(
    collapse?.defaultCollapsed ?? false
  )

  const collapsible = Boolean(collapse?.active)

  const padding = disablePadding ? 0 : size === 'small' ? 1 : 2
  const paddingTop = !header ? padding : size === 'small' ? 0.5 : 1

  const handleToggle = () => {
    if (collapse && collapsible) {
      if (collapsed && collapse?.onExpand) {
        collapse.onExpand()
      }
      if (!collapsed && collapse?.onCollapse) {
        collapse.onCollapse()
      }

      setCollapsed(!collapsed)
    }
  }

  useEffect(() => {
    if (collapse?.collapsed !== undefined) {
      setCollapsed(collapse.collapsed)
    }
  }, [collapse?.collapsed])

  return (
    <ExternalHeaderWrapper
      externalHeader={externalHeader}
      gutterBottom={gutterBottom}
      sx={sx}
    >
      <Box
        id={id}
        onClick={collapsed ? handleToggle : undefined}
        ref={ref}
        sx={{
          bgcolor:
            color === 'transparent'
              ? undefined
              : ['default', 'paper'].includes(color)
                  ? `background.${color}`
                  : `${color}.background`,
          borderRadius: 1,
          border: editMode || border ? '1px solid' : undefined,
          borderColor: editMode ? 'primary' : 'divider',
          display: 'flex',
          flexDirection: 'column',
          ...(!externalHeader
            ? { mb: gutterBottom ? 1 : undefined, ...sx }
            : null)
        }}
      >
        {header ? (
          <Box
            onClick={handleToggle}
            sx={{
              bgcolor: `background.${color}`,
              borderRadius: 'inherit',
              cursor: collapsible ? 'pointer' : undefined,
              px: disablePadding ? 0 : size === 'small' ? 1 : 2,
              py: size === 'small' ? 0.5 : 1,
              pt: !collapsible && size !== 'small' ? 2 : undefined,
              position: 'sticky',
              top: 0,
              transition: 'all 0.5s ease',
              zIndex: 1,
              ['&:hover .ContentBoxCollapseIcon']: {
                transform:
                  collapsible && collapsed ? 'rotate(45deg)' : undefined
              }
            }}
          >
            {typeof header === 'object' && 'title' in header ? (
              <SectionHeader
                {...header}
                actions={
                  <>
                    {header.actions}{' '}
                    {collapsible ? (
                      <IconButton
                        color={editMode ? 'primary' : undefined}
                        onClick={handleToggle}
                        size="small"
                      >
                        <Icon
                          className="ContentBoxCollapseIcon"
                          fontSize="small"
                          name={'ChevronRight'}
                          sx={{
                            transition: 'all 0.2s ease',
                            transform: collapsed
                              ? 'rotate(0deg)'
                              : 'rotate(90deg)'
                          }}
                        />
                      </IconButton>
                    ) : null}
                  </>
                }
                divider={collapsible && collapsed ? false : header.divider}
              />
            ) : (
              header
            )}
          </Box>
        ) : null}
        <ContentWrapper
          collapsed={collapsed}
          collapsible={collapsible}
          unmountOnExit={Boolean(collapse?.unmountOnExit)}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: padding,
              position: 'relative',
              // A border, not padding, so a sticky child sticks at the top.
              pt: 0,
              borderTop: `${paddingTop * 8}px solid transparent`
            }}
          >
            {helperText ? (
              <Text
                color="text.secondary"
                component="div"
                sx={{ mb: 2 }}
                variant="body2"
              >
                {helperText}
              </Text>
            ) : null}
            {children}
          </Box>
        </ContentWrapper>
        {footer ? (
          <Box
            sx={{
              px: disablePadding ? 0 : size === 'small' ? 1 : 2,
              py: disablePadding ? 0 : size === 'small' ? 0.5 : 1
            }}
          >
            {footer}
          </Box>
        ) : null}
      </Box>
    </ExternalHeaderWrapper>
  )
})

interface ContentWrapperProps {
  children: ReactNode
  collapsed: boolean
  collapsible: boolean
  unmountOnExit: boolean
}

const ContentWrapper = (props: ContentWrapperProps) => {
  const { children, collapsed, collapsible } = props

  if (collapsible)
    return (
      <Collapse in={!collapsed} unmountOnExit={true}>
        {children}
      </Collapse>
    )

  return <>{children}</>
}

interface ExternalHeaderWrapperProps
  extends Pick<ContentBoxProps, 'externalHeader' | 'gutterBottom' | 'sx'> {
  children: ReactNode
}

const ExternalHeaderWrapper = React.forwardRef(
  (props: ExternalHeaderWrapperProps, ref: any) => {
    const { children, externalHeader, gutterBottom, sx } = props

    if (externalHeader)
      return (
        <Box ref={ref} sx={{ mb: gutterBottom ? 1 : undefined, ...sx }}>
          {externalHeader ? (
            <SectionHeader gutterBottom={true} {...externalHeader} />
          ) : null}
          {children}
        </Box>
      )

    return <>{children}</>
  }
)

export default ContentBox
